import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react"
import React from "react"

import { matchString } from "shared/helpers/stringMatcher"

type AccordionProps = {
    children:
        | React.ReactElement<AccordionItemProps>
        | React.ReactElement<AccordionItemProps>[]
}

// TODO: move shared constants/functions to separate file
// eslint-disable-next-line react-refresh/only-export-components
export const itemState = {
    Open: "Open",
    Closed: "Closed",
    ForcedOpen: "ForcedOpen",
    ForcedClosed: "ForcedClosed",
} as const

export type ItemState = (typeof itemState)[keyof typeof itemState]

type AccordionItemProps = {
    title: string | React.ReactNode
    state: ItemState
    onToggle: (currentState: ItemState, nextState: ItemState) => void
    children: React.ReactNode
    statusBorder?: boolean
    icon?: boolean
}

export function Accordion({ children }: AccordionProps) {
    return (
        <div className="border-border-tertiary rounded-tl-md rounded-tr-md border">
            {children}
        </div>
    )
}

export function AccordionItem({
    title,
    state,
    onToggle,
    children,
    statusBorder = true,
    icon = false,
}: AccordionItemProps) {
    const nextState = matchString<ItemState, ItemState>({
        Open: itemState.ForcedClosed,
        ForcedOpen: itemState.Closed,
        Closed: itemState.ForcedOpen,
        ForcedClosed: itemState.Open,
    })

    const onClick = () => onToggle(state, nextState(state))
    return matchString<ItemState, React.ReactNode>({
        Open: (
            <>
                <button
                    onClick={onClick}
                    className={`bg-surface-tertiary hover:bg-surface-hover border-border-tertiary flex w-full cursor-pointer items-center justify-between border-t-1 border-b-1 border-solid px-4 py-2 text-left font-medium first:border-t-0 last:border-b-0 ${
                        statusBorder &&
                        "border-l-status-default-soft border-l-4"
                    }`}
                >
                    <span className="flex-1">{title}</span>
                    {icon && (
                        <CaretUpIcon
                            weight="bold"
                            size={16}
                            className="min-w-4"
                        />
                    )}
                </button>
                <OpenContent>{children}</OpenContent>
            </>
        ),
        Closed: (
            <>
                <button
                    onClick={onClick}
                    className={`bg-surface-muted hover:bg-surface-hover border-border-tertiary flex w-full cursor-pointer items-center justify-between border-t-1 border-b-1 border-solid px-4 py-2 text-left font-medium first:border-t-0 last:border-b-0 ${
                        statusBorder && "border-l-status-info-soft border-l-4"
                    }`}
                >
                    <span className="flex-1">{title}</span>
                    {icon && (
                        <CaretDownIcon
                            weight="bold"
                            size={16}
                            className="min-w-4"
                        />
                    )}
                </button>
                <ClosedContent>{children}</ClosedContent>
            </>
        ),
        ForcedOpen: (
            <>
                <button
                    onClick={onClick}
                    className={`bg-surface-tertiary hover:bg-surface-hover border-border-tertiary flex w-full cursor-pointer items-center justify-between border-t-1 border-b-1 border-solid px-4 py-2 text-left font-medium first:border-t-0 last:border-b-0 ${
                        statusBorder &&
                        "border-l-status-success-soft border-l-4"
                    }`}
                >
                    <span className="flex-1">{title}</span>
                    {icon && (
                        <CaretUpIcon
                            weight="bold"
                            size={16}
                            className="min-w-4"
                        />
                    )}
                </button>
                <OpenContent>{children}</OpenContent>
            </>
        ),
        ForcedClosed: (
            <>
                <button
                    onClick={onClick}
                    className={`bg-surface-muted hover:bg-surface-hover border-border-tertiary flex w-full cursor-pointer items-center justify-between border-t-1 border-b-1 border-solid px-4 py-2 text-left font-medium first:border-t-0 last:border-b-0 ${
                        statusBorder && "border-l-status-error-soft border-l-4"
                    }`}
                >
                    <span className="flex-1">{title}</span>
                    {icon && (
                        <CaretDownIcon
                            weight="bold"
                            size={16}
                            className="min-w-4"
                        />
                    )}
                </button>
                <ClosedContent>{children}</ClosedContent>
            </>
        ),
    })(state)
}

function OpenContent({ children }: { children: React.ReactNode }) {
    return (
        <div className="bg-surface-primary overflow-hidden">
            <div className="p-4">{children}</div>
        </div>
    )
}

function ClosedContent(_: { children: React.ReactNode }) {
    return <></>
}
